/**
 * Hoodsly store logic, kept out of the SDK glue on purpose.
 *
 * expivi-viewer.js is written against a specific @expivi/sdk: useStoreAttributes,
 * useAttribute, the configurator lifecycle, the viewer bridge. An SDK upgrade
 * rewrites that file. Everything in HERE is the store's own rules - what a
 * wholesale buyer pays, which colours a company may see, what rides on the cart
 * line - and none of it touches an SDK symbol, so an upgrade cannot take it out
 * with the glue.
 *
 * Ported from hub-api-shopify/expivi-theme (V1). The pricing half already existed
 * in expivi-viewer.js byte-identical to V1 and is MOVED here rather than copied,
 * so there is one definition, not two that drift. The render-capture half is new
 * to V2: V1 could not be ported while the V2 theme had no 3D viewer and therefore
 * no screenshot to take. It has one now, and window.expivi.getScreenshot is live.
 *
 * Load order matters - the snippet renders this BEFORE expivi-viewer.js, which
 * reads window.ExpiviHoodsly at boot. Both are `defer`, so order is preserved.
 *
 * VerifyOrderPricing.php mirrors the pricing maths server-side. The two MUST
 * agree or wholesale + add-on orders are held at orders/paid.
 */
(function () {
    'use strict';

    // ---------------------------------------------------------------
    // Pricing. Moved from expivi-viewer.js; identical to V1.
    // ---------------------------------------------------------------

    /**
     * Retail total minus the buyer's wholesale %, rounded to 2 decimals.
     * percent 0 (D2C / non-company) rounds and returns the total.
     */
    function discountedUnitPrice(total, percent) {
        var pct = Number(percent) || 0;
        return Math.round(total * (1 - pct / 100) * 100) / 100;
    }

    /**
     * The NET price the buyer pays: the wholesale % discounts ONLY the hood
     * (total minus the discount-excluded add-ons); the add-ons are added back at
     * full price.
     *   net = round((total - excludedTotal) * (1 - pct/100) + excludedTotal, 2)
     * Rounds unconditionally. There used to be a "pct 0 and no add-ons returns the
     * total untouched" shortcut, which was arithmetically identical for a
     * cent-precise total and WRONG for a sub-cent one: unitPriceProperties rounds
     * retail, so an unrounded net made retail > net and emitted a phantom
     * _unit_discount on a D2C line — a retail buyer would see a "Wholesale
     * discount". VerifyOrderPricing has no such shortcut either; it recomputes
     * excludedTotal independently and expects this exact formula, and the two
     * MUST agree or wholesale+add-on orders are held.
     */
    function discountedWithAddons(total, excludedTotal, percent) {
        var pct = Number(percent) || 0;
        var ex = Number(excludedTotal) || 0;
        return Math.round(((total - ex) * (1 - pct / 100) + ex) * 100) / 100;
    }

    /**
     * Split that net price into the TWO line properties the cart needs, so the
     * reduction shows as a real discount everywhere instead of a silently
     * cheaper line:
     *   _unit_price    — RETAIL. The expivi-dynamic-price Cart Transform charges
     *                    the line at this, so it becomes the "before" price.
     *   _unit_discount — the per-unit reduction the expivi-wholesale-discount
     *                    function subtracts as a product discount, which Shopify
     *                    then renders on the cart, checkout, order details, order
     *                    confirmation emails and admin. Omitted when zero, so a
     *                    D2C line is byte-identical to the pre-split build.
     * price - discount === discountedWithAddons(...) exactly at cent granularity:
     * the buyer pays what they paid before the split, it is only presented
     * differently. Returns strings — line properties are strings.
     */
    function unitPriceProperties(total, excludedTotal, percent) {
        var t = Number(total) || 0;
        var retail = Math.round(t * 100) / 100;
        var net = discountedWithAddons(t, excludedTotal, percent);
        return {
            price: String(retail),
            discount: retail > net ? (retail - net).toFixed(2) : null,
        };
    }

    /**
     * Category Discount Percentages cap: a company's wholesale % is pulled down
     * to a merchant-configured collection's discount % (never boosted above it)
     * for any product in that collection. `cap` is the resolved value from the
     * theme's `data-category-discount-cap`; < 0 means "not in any configured
     * collection" and leaves the % untouched.
     *
     * Mirrors ExpiviClient::cappedDiscountPercent in app/Jobs/VerifyOrderPricing.php.
     */
    function cappedWholesalePercent(percent, categoryCap) {
        var pct = Number(percent) || 0;
        var cap = Number(categoryCap);
        if (!isFinite(cap) || cap < 0) {
            return pct;
        }
        return Math.min(pct, cap);
    }

    // Sum of the add-on group amounts ({label -> amount}).
    function excludedSum(groupPrices) {
        var s = 0;
        Object.keys(groupPrices || {}).forEach(function (k) {
            s += Number(groupPrices[k]) || 0;
        });
        return s;
    }

    /**
     * The breakdown amounts from a total, its add-on group prices and the
     * wholesale %. Add-ons are EXCLUDED from the discount (ADR-0006):
     *   hood        = total - excludedTotal       (retail hood, the discount base)
     *   discounted  = hood * (1 - percent/100)
     *   groups      = {label -> amount}           (never discounted)
     *   total       = discountedWithAddons(...)   (what the buyer pays)
     * Pure (no DOM) so the node assert check can exercise it.
     */
    function breakdownAmounts(total, groupPrices, percent) {
        var groups = groupPrices || {};
        var ex = excludedSum(groups);
        var hood = total - ex;
        var discounted = discountedUnitPrice(hood, percent);
        // Total is computed the SAME single-step way as the cart line + verifier
        // (discountedWithAddons), not discounted + ex — the two-step form can
        // differ by $0.01 on a half-cent tie and break the "breakdown can never
        // disagree with the charge" invariant.
        return {
            hood: hood,
            discounted: discounted,
            groups: groups,
            excludedTotal: ex,
            total: discountedWithAddons(total, ex, percent),
        };
    }

    /**
     * The company a CSR picked in csr-draft-order.liquid, or '' for an ordinary
     * buyer. A CSR belongs to no company, so Liquid's customer.current_company is
     * nil and companyPresent is false — without this every line they configure on
     * behalf of a company is priced at retail. localStorage, not sessionStorage:
     * the pick happens on the cart page and is needed here, and a product page
     * opened in a new tab starts with an empty sessionStorage. (The writing
     * snippet is v1-only today; this is inert until it is ported.)
     */
    function actingCompanyId() {
        try {
            var saved = JSON.parse(localStorage.getItem('hub-csr-company') || 'null');
            return saved && saved.id ? String(saved.id) : '';
        } catch (e) {
            return '';
        }
    }

    /**
     * The acting company's B2B wholesale discount %, via the app proxy (US-002).
     * Only called for company buyers or a CSR acting for one. Fails open to 0
     * (retail) on any error, so a proxy hiccup never changes the price the buyer
     * expected — including the 403 a non-CSR gets from a stale hub-csr-company.
     */
    function fetchWholesaleDiscount(companyPresent, variantId) {
        // A real company buyer always gets their OWN %, never a pick left in the
        // tab by a CSR who logged out before them: that entry earns them a 403,
        // which fails open to retail, and they are OVERCHARGED — caught by
        // VerifyOrderPricing, but only after they have paid.
        var actingId = companyPresent ? '' : actingCompanyId();
        if (!companyPresent && !actingId) {
            return Promise.resolve(0);
        }
        // variant_id lets the backend resolve the rate when the location has
        // several percentage catalogs (rate is then per-product); ignored otherwise.
        //
        // A company buyer with NO variant is a bug, not a retail shopper. On a
        // multi-catalog location the backend answers 0.0 without it — the
        // documented "no discount rather than a wrong one" fail-safe — so the
        // shopper is quietly shown and charged RETAIL with nothing to show for
        // it. Seen live: the same customer gets percent 0 bare and percent 30
        // with the variant. Say so rather than let it pass as a normal retail
        // price; the call still goes out, because a wrong price beats no price
        // block at all.
        var query = [];
        if (actingId) { query.push('company_id=' + encodeURIComponent(actingId)); }
        if (variantId) {
            query.push('variant_id=' + encodeURIComponent(variantId));
        } else {
            // eslint-disable-next-line no-console
            console.warn('[expivi] wholesale % requested with no variant id — a '
                + 'multi-catalog company location will resolve to 0% (retail). '
                + 'Check data-variant-id on the configurator element.');
        }
        return fetch('/apps/hub/wholesale-discount' + (query.length ? '?' + query.join('&') : ''), {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        })
            .then(function (r) {
                return r.ok ? r.json() : null;
            })
            .then(function (b) {
                return (b && b.success && Number(b.percent)) || 0;
            })
            .catch(function () {
                return 0;
            });
    }


    // ---------------------------------------------------------------
    // Raw price items, straight off the API response.
    //
    // Written when the SDK's mapPriceableType returned null for any
    // priceable_type outside base_price|attributes|script_engine|bom, and
    // mapResponse FILTERED the nulls out: a catalogue sending
    // "attribute_values" lost every item, `groups` arrived empty, `total`
    // survived (it is read from `total_amount`, not summed from the items) and
    // excludedSum() was 0 — which silently priced discount-excluded add-ons
    // INSIDE the wholesale discount (a $700 ventilation went out $210 light).
    //
    // VERIFIED 2026-09-11: 1.5.0 and 1.7.0 BOTH map attribute_values
    // (`Cm=["base_price","attributes","attribute_values","bom","script_engine"]`),
    // so on those bundles `groups` arrives populated and the fallback below
    // never runs. Keep it anyway — it costs nothing and covers the older
    // bundles plus any priceable_type a future catalogue adds.
    //
    // ponytail: the fallback is all-or-nothing — it triggers only when the SDK
    // mapped NOTHING. A catalogue mixing a mapped type with an unmapped one
    // loses the unmapped items silently, and those add-ons then ride inside the
    // discount. Per-item reconciliation if a catalogue ever mixes them.
    //
    // So we keep the response's own items. This reads the API payload, not an
    // SDK symbol, so an SDK upgrade cannot take it out with the glue; and if a
    // later SDK maps them properly, groupPricesFromBreakdown prefers its groups
    // and never looks here.
    //
    // ponytail: XHR only — axios uses its XHR adapter here (verified live: 4
    // XHR hits, 0 fetch). If it ever switches to the fetch adapter this goes
    // quiet, so the empty-breakdown guard in addToCart is what must fail
    // closed; do not let this be the only thing standing between a shopper and
    // an undercharge.
    // ---------------------------------------------------------------
    var PRICE_PATH = '/view/product-offers/prices/generate';
    var VALUES_PATH = '/attribute-values';
    var priceItems = {};
    var valuePrices = {};

    function rememberPriceItems(text) {
        try {
            var prices = (JSON.parse(text).data || {}).prices || [];
            prices.forEach(function (p) {
                if (p && p.product_cid) {
                    priceItems[String(p.product_cid)] = p.items || [];
                }
            });
        } catch (e) {
            /* not a price payload — nothing to keep */
        }
    }

    /**
     * {attribute value id => price_adjustment}, from the attribute-values
     * response — what each choice costs, which is the only place the catalogue
     * publishes it.
     *
     * The SDK drops it: mapAttributeValueGet runs every row through a mapper
     * that builds {id, identifier, description, status, thumbnailUrl, config,
     * ...} and `price_adjustment` is not among the fields it copies, so by the
     * time the values reach useAttribute().values the number is gone. Same class
     * of loss as the price items above, and the same fix.
     *
     * Accumulated, never replaced: the SDK requests values per attribute (and
     * pages at 50), so each response carries one slice of the catalogue.
     */
    function rememberValuePrices(text) {
        try {
            var rows = JSON.parse(text).data || [];
            rows.forEach(function (row) {
                // Two documented shapes: [{attribute_id, attribute_values}] and
                // a bare {attributeId: page} map. Only the values matter here,
                // so take whichever of the two carries them.
                var pages = (row && row.attribute_values)
                    ? [row.attribute_values]
                    : Object.keys(row || {}).map(function (k) { return row[k]; });

                pages.forEach(function (page) {
                    ((page && page.data) || []).forEach(function (v) {
                        if (v && v.id != null && v.price_adjustment != null) {
                            valuePrices[String(v.id)] = Number(v.price_adjustment) || 0;
                        }
                    });
                });
            });
        } catch (e) {
            /* not an attribute-values payload — nothing to keep */
        }
    }

    /** The price_adjustment for one value id, or null when none was published. */
    function valuePrice(valueId) {
        var key = String(valueId);
        return Object.prototype.hasOwnProperty.call(valuePrices, key) ? valuePrices[key] : null;
    }

    /**
     * The items from the last price response for a product, or [] when none has
     * landed yet. Shape is the API's, not the SDK's: {identifier, name, amount,
     * priceable_type}.
     */
    function rawPriceItems(productCid) {
        return priceItems[String(productCid)] || [];
    }

    function observePriceItems() {
        if (typeof XMLHttpRequest === 'undefined' || observePriceItems.installed) {
            return;
        }
        observePriceItems.installed = true;
        var open = XMLHttpRequest.prototype.open;
        var send = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function (method, url) {
            var href = String(url || '');
            this.__expiviPriceCall = href.indexOf(PRICE_PATH) !== -1;
            // endsWith, not indexOf: the price path also contains the word
            // "product-offers", and only this one ends in /attribute-values.
            this.__expiviValuesCall = href.slice(-VALUES_PATH.length) === VALUES_PATH;
            return open.apply(this, arguments);
        };
        XMLHttpRequest.prototype.send = function () {
            var xhr = this;
            if (xhr.__expiviPriceCall) {
                xhr.addEventListener('load', function () {
                    rememberPriceItems(xhr.responseText);
                });
            }
            if (xhr.__expiviValuesCall) {
                xhr.addEventListener('load', function () {
                    rememberValuePrices(xhr.responseText);
                });
            }
            return send.apply(this, arguments);
        };
    }

    /**
     * The SKU segments for a configuration, in configurator order.
     *
     * V1 shipped `_expivi_sku_keys` because its short codes lived only in the
     * catalogue bootstrap, which the server never saw. V2 needs it for a different
     * reason: the Master SKU comes from the BOM, and when the BOM is unavailable
     * the line carries nothing identifying the build at all.
     *
     * Each segment carries who it came from as well as the code:
     *   label      - the attribute's display name ("Select Size")
     *   id         - the attribute's ULID, stable across renames
     *   identifier - the attribute's own identifier ("hood_size", or a 32-hex
     *                hash for one nobody named)
     *   sku        - the chosen value's identifier, the SKU segment itself
     *
     * All four, not just one: `label` is what a human reads on a cart line or in
     * HubSpot, but it is merchant-renameable AND locale-dependent (the SDK is
     * built with `locale`), so nothing server-side may key on it. `id` and
     * `identifier` are the stable halves. Same reasoning as
     * allowedColorIdentifiers, which refuses to match on names for exactly this.
     *
     * Still a LIST, not a map. Order is the segment order, and a list is the only
     * shape that survives the hub: SkuGeneratorService::configuredAttributeIds
     * casts the attribute id to (int) — every V2 ULID casts to 0 — so a map keyed
     * by id collides, and a map keyed by name comes back out of `ksort` in
     * ALPHABETICAL order rather than configurator order. A JSON array decodes to
     * PHP keys 0..n and ksort leaves those alone.
     *
     * One entry per chosen value, so a multi-select contributes a segment each,
     * exactly as the old flat list did.
     *
     * ponytail: a 32-hex identifier is Expivi's generated id for a value nobody
     * gave a code to ("Custom Color Match"), never a SKU segment, so it is
     * dropped. Anything else is passed through - deciding which attributes
     * "should" contribute is the catalogue's call, not this file's. The filter is
     * on the VALUE's identifier only; an attribute with a hashed identifier of
     * its own still contributes, it just carries the hash in `identifier`.
     */
    var GENERATED_ID = /^[0-9a-f]{32}$/i;

    function configuredSkuCodes(attributes, ignored, priceOf) {
        var segments = [];
        // The admin's "Ignored Expivi IDs" (hub.ignored_sku_codes): ids Expivi made
        // up that are not SKUs. Same skip the Hub applies server-side.
        var skip = (Array.isArray(ignored) ? ignored : []).map(function (s) {
            return String(s || '').trim().toLowerCase();
        });
        (attributes || []).forEach(function (a) {
            ((a && a.configuredValue) || []).forEach(function (v) {
                var sku = String((v && v.identifier) || '').trim();
                if (!sku || GENERATED_ID.test(sku) || skip.indexOf(sku.toLowerCase()) !== -1) {
                    return;
                }
                var segment = {
                    label: String((a && a.name) || '').trim(),
                    id: String((a && a.id) || ''),
                    identifier: String((a && a.identifier) || ''),
                    sku: sku,
                };
                // The value's own list price (what the shopper sees as "+ $X"),
                // retail and before any wholesale %/rush adjustment. Informational:
                // the verifier recomputes from the configuration.
                if (typeof priceOf === 'function') {
                    segment.price = Number(priceOf(v)) || 0;
                }
                segments.push(segment);
            });
        });
        return segments;
    }

    /**
     * Port of SkuGeneratorService::resolveMainSku() (hub-api-shopify): a product
     * SKU collapses to the admin-configured main SKU it starts with
     * (CCXRAW30 -> CCX), case-insensitively, longest entry first so CCXL wins
     * over CCX. Returns the entry as configured; no match -> the SKU unchanged.
     */
    function resolveMainSku(sku, mainSkus) {
        var s = String(sku || '').trim().replace(/^VEN-/, '');
        var lower = s.toLowerCase();
        var list = (Array.isArray(mainSkus) ? mainSkus : [])
            .map(function (m) { return String(m || '').trim(); })
            .filter(Boolean)
            .sort(function (a, b) { return b.length - a.length; });
        for (var i = 0; i < list.length; i++) {
            if (lower.indexOf(list[i].toLowerCase()) === 0) {
                return list[i];
            }
        }
        return s;
    }

    /** Hub-style sku_gen preview: main SKU first, then the segments, de-duplicated. */
    function buildDebugSku(productSku, mainSkus, codes) {
        var segs = [resolveMainSku(productSku, mainSkus)]
            .concat((codes || []).map(function (c) { return c.sku; }))
            .filter(Boolean);
        return segs.filter(function (s, i) { return segs.indexOf(s) === i; }).join('-');
    }

    // ---------------------------------------------------------------
    // Colour gating. Reads only .identifier / .name off an attribute,
    // so it survives the SDK reshaping everything around it.
    // ---------------------------------------------------------------

    /**
     * No hardcoded attribute names. Which configurator option carries which kind
     * of colour is merchant data (shop metafield expivi.catalogs.color_attributes,
     * edited on the app's Colors page), because those labels live in Expivi and get
     * renamed - "Select Chimney Color (Ryan)" today, something else next week - and
     * a rename must not need a theme deploy.
     *
     * Guessing from the name was considered and rejected: on the live catalogue
     * "Apron SW/BM Color Code Options" and "Apron Physical Color Match Options"
     * both contain apron AND color, and neither is a colour list.
     */

    function normalizeKey(v) {
        return String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    /**
     * The set of value identifiers this attribute may show, or null for "not a
     * colour attribute, or nothing configured — show everything".
     *
     * Membership only. Labelling is not this function's job: Expivi carries its
     * own label on each value (`config.value`), which valueLabel() uses, so the
     * palette's colour name is deliberately NOT substituted here — two sources for
     * one string is a drift waiting to happen.
     *
     * `colors` is the resolved allow-list from the snippet: the default catalog
     * plus whatever the signed-in company's own catalogs add. An empty list means
     * the shop has no catalogs set up, which must not black out every swatch.
     */
    /**
     * The value to move to when the current selection is one this shopper is not
     * entitled to, or null when nothing needs changing.
     *
     * Gating only filters what the panel RENDERS. The catalogue's own default
     * arrives already selected, so a colour outside the company's list sits in the
     * configuration and rides to the cart - seen live, an ACME order carrying ARAW
     * when ACME may only have the three Sonoma colours. Without this the gating is
     * decorative.
     *
     * Moves to the first allowed value rather than clearing: Expivi wants a
     * selection, and an empty one prices the configuration differently and makes
     * the shopper re-pick something they never touched.
     */
    /**
     * cid of the live value to re-select when a snapshot-restored single value
     * carries an identifier/label that differs from the catalogue's today (SDK
     * parse() copies both from the frozen snapshot), else null. Null too when
     * the live value is not among `lookup` — leave what we cannot see alone.
     */
    function staleSnapshotValueCid(configuredValue, lookup) {
        var current = (configuredValue || [])[0];
        if (!current || !current.id) {
            return null;
        }
        var live = (lookup || []).filter(function (v) {
            return v && v.id === current.id;
        })[0];
        if (!live || !live.cid) {
            return null;
        }
        var liveLabel = (live.config && live.config.value) || live.value || '';
        var same = String(live.identifier || '') === String(current.identifier || '')
            && (!liveLabel || String(liveLabel) === String(current.value || ''));
        return same ? null : live.cid;
    }

    /**
     * A snapshot that lands after the load gave up and built the panel with
     * defaults must not overwrite a shopper who has already started choosing:
     * parse() is destructive. Untouched, it still applies (the shopper then gets
     * the product they clicked, which is the point of the snapshot).
     */
    function shouldApplySnapshot(state) {
        return !(state && state.gaveUp && state.touched);
    }

    function correctedSelection(configuredValue, values, allowed, lookup) {
        if (!allowed) {
            return null;
        }
        var current = (configuredValue || [])[0];
        // A value restored from a snapshot carries the snapshot's frozen
        // `identifier`/`value` (SDK parse() copies them), not the catalogue's
        // today. When Expivi swaps a hash identifier for a SKU the stale hash
        // fails the allow-list and a valid selection gets replaced. Judge the
        // live value (same cid) when it is on the loaded page.
        // `lookup` is every value fetched so far (a colour on page 3 is not in
        // `values`, the current page).
        var live = current && (lookup || []).concat(values || []).filter(function (v) {
            return v && v.cid === current.cid;
        })[0];
        if (!current || isAllowedColor(live || current, allowed)) {
            return null;
        }
        var candidates = (values || []).filter(function (v) {
            return isAllowedColor(v, allowed);
        });
        // Prefer a strict allow-list hit over an always-shown fallback: Raw /
        // Custom sit on page 1 while the catalog colour may load later, and
        // taking whichever comes first would lock onto Raw via the caller's
        // once-only latch.
        var replacement = candidates.filter(function (v) {
            return String((v && v.identifier) || '') in allowed;
        })[0] || candidates[0];

        return replacement ? replacement.cid : null;
    }

    /**
     * "Raw (Unfinished)" and "Custom Color Match" are offered to every company
     * whatever catalogs it holds. Matched on the value's LABEL because Custom
     * Color Match has only a generated 32-hex identifier (no SKU to put in a
     * catalog); a rename in Expivi needs this updated.
     */
    var ALWAYS_SHOWN_KEYS = ['rawunfinished', 'customcolormatch'];
    var ALWAYS_SHOWN = new RegExp('^(' + ALWAYS_SHOWN_KEYS.join('|') + ')$');

    function isAlwaysShown(v) {
        var label = (v && v.config && v.config.value) || (v && (v.value || v.description)) || '';
        return ALWAYS_SHOWN.test(normalizeKey(label));
    }

    /** In the allow-list, or one of the always-offered values. */
    function isAllowedColor(v, allowed) {
        return isAlwaysShown(v) || (!!allowed && String((v && v.identifier) || '') in allowed);
    }

    function allowedColorIdentifiers(view, colors, attributeMap) {
        if (!colors || !colors.length || !attributeMap) {
            return null;
        }

        // Matched on the normalised label so trailing spaces or a case change in
        // Expivi do not silently turn the gating off.
        var field = null;
        var wanted = normalizeKey(view.name) || normalizeKey(view.identifier);
        Object.keys(attributeMap).forEach(function (line) {
            (attributeMap[line] || []).forEach(function (label) {
                if (normalizeKey(label) === wanted) {
                    field = line;
                }
            });
        });
        if (!field) {
            return null;
        }
        var allowed = {};
        colors.forEach(function (c) {
            var sku = c && c[field];
            if (sku) {
                allowed[String(sku)] = true;
            }
        });
        return allowed;
    }


    // ---------------------------------------------------------------
    // Configured cart render, ported from V1. Shopify gives the
    // storefront no way to set a line image, so the render is
    // uploaded and its url rides on the line as _preview_url.
    // ---------------------------------------------------------------

    // Nothing the add to cart awaits may outlast these.
    var CAPTURE_TIMEOUT_MS = 2000;
    var FRAME_FALLBACK_MS = 250;
    // Image decode only (already in memory, no network) — a few ms in
    // practice. Bounded anyway so a decode hiccup can't cost anything either.
    var BLANK_CHECK_TIMEOUT_MS = 500;

    /**
     * Two frames, or a timer if frames are not coming. `requestAnimationFrame`
     * does not fire in a backgrounded tab, so waiting on it alone is a hang.
     */
    function nextFrames() {
        return new Promise(function (resolve) {
            var done = false;
            var finish = function () { if (!done) { done = true; resolve(); } };
            setTimeout(finish, FRAME_FALLBACK_MS);
            if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(function () { requestAnimationFrame(finish); });
            }
        });
    }

    /**
     * Resolve with `fallback` if the promise has not settled in time, and never
     * reject. Used to bound anything the add to cart waits on.
     */
    function settleWithin(promise, ms, fallback) {
        return new Promise(function (resolve) {
            var done = false;
            var finish = function (value) { if (!done) { done = true; resolve(value); } };
            setTimeout(function () { finish(fallback); }, ms);
            Promise.resolve(promise).then(finish, function () { finish(fallback); });
        });
    }

    function poseOf(t) {
        if (!t) {
            return null;
        }
        var axes = ['x', 'y', 'z'];
        var read = function (v) { return v ? axes.map(function (a) { return v[a]; }) : null; };
        return {
            rotation: read(t.mRotation),
            position: read(t.mPosition),
            scale: read(t.mScale),
            quaternion: t.mQuaternion ? ['x', 'y', 'z', 'w'].map(function (a) { return t.mQuaternion[a]; }) : null,
        };
    }

    function applyPose(t, pose) {
        if (!t || !pose) {
            return;
        }
        [['mRotation', pose.rotation], ['mPosition', pose.position], ['mScale', pose.scale]].forEach(function (pair) {
            if (t[pair[0]] && pair[1]) {
                ['x', 'y', 'z'].forEach(function (axis, i) { t[pair[0]][axis] = pair[1][i]; });
            }
        });
        if (t.mQuaternion && pose.quaternion) {
            ['x', 'y', 'z', 'w'].forEach(function (axis, i) { t.mQuaternion[axis] = pose.quaternion[i]; });
        }
        // Without this the viewer keeps its cached matrix and the screenshot
        // comes back at the old pose.
        t.matrixNeedsUpdate = true;
    }

    // 'FIT_OBJECT' is not a real framing value: the viewer treats it as the
    // default (the scene's fixed camera), which never refits. Measured live at
    // h=1000, hood bottom pinned near y=900: a 30" tall hood is ~635px, so
    // anything over ~42" clips off the top (36x48 did). Fix: shrink the product
    // nodes to ~36" tall for the shot (scale bottom-anchored, restored after),
    // and size the canvas to the hood's width: px width / h ~= 0.706 at 36",
    // +0.0148 per extra inch, x scale, +20% margin.
    // Size read from the selected option ('36" Width x 48" Height'); no match
    // -> the old 1300x1000 at scale 1.
    var SHOT_MAX_H = 36;
    function shotSize() {
        // The size control is a checked radio card on some products and a
        // dropdown button on others; other radios (extension, trim...) are
        // checked too, so take the first one whose text reads W x H.
        var w, h;
        var els = document.querySelectorAll('input:checked, button, [role=combobox]');
        for (var i = 0; i < els.length && !(w && h); i++) {
            var text = (els[i].tagName === 'INPUT' ? (els[i].closest('label') || els[i].parentElement || {}) : els[i]).textContent || '';
            w = /(\d+)\D{0,3}\s*Width\s*x\s*(\d+)\D{0,3}\s*Height/i.exec(text);
            if (w) {
                h = [0, w[2]];
            }
        }
        if (!w || !h) {
            return { w: 1300, h: 1000, scale: 1 };
        }
        var scale = Math.min(1, SHOT_MAX_H / Number(h[1]));
        var objW = (0.706 + 0.0148 * (Number(w[1]) - 36)) * scale;
        return { w: Math.round(800 * Math.max(objW * 1.2, 0.7)), h: 800, scale: scale };
    }

    function scaleNodes(list, s) {
        if (s >= 1) {
            return;
        }
        list.forEach(function (node) {
            var sc = node.mTransform && node.mTransform.mScale;
            if (sc) {
                sc.x *= s; sc.y *= s; sc.z *= s;
                node.mTransform.matrixNeedsUpdate = true;
            }
        });
    }

    function capturePose(api) {
        return Promise.resolve(api.resolveProductNodes())
            .then(function (nodes) {
                var list = nodes || [];
                if (!list.length) {
                    return null;
                }
                var saved = list.map(function (node) { return poseOf(node.mTransform); });
                // Hide the viewer while it is posed/shrunk for the shot so the
                // shopper doesn't see it jump (screenshot renders the same
                // at opacity 0 — verified). Shown again a couple of frames
                // after the pose is restored; the timer is the backstop if a
                // hung capture never reaches restore().
                var cont = document.querySelector('[data-expivi-viewer-container]');
                var showViewer = function () { if (cont) { cont.style.opacity = ''; } };
                if (cont) {
                    cont.style.opacity = '0';
                    setTimeout(showViewer, 3000);
                }
                var restore = function () {
                    list.forEach(function (node, i) { applyPose(node.mTransform, saved[i]); });
                    nextFrames().then(showViewer, showViewer);
                };

                list.forEach(function (node) {
                    applyPose(node.mTransform, poseOf(node.mInitialTransform));
                });

                // Two frames: one for the transform to be consumed, one for the
                // draw. Measured at ~79ms including the screenshot, against
                // ~120ms for a 50ms timer — and a timer is a guess either way.
                //
                var size = shotSize();
                scaleNodes(list, size.scale);
                return nextFrames()
                    .then(function () { return api.getScreenshot(size.w, size.h, 'FIT_OBJECT'); })
                    .then(function (shot) { restore(); return shot || null; }, function () { restore(); return null; });
            })
            .catch(function () { return null; });
    }

    /**
     * A real render has edges and shading; a scene that never drew anything
     * (camera pointed at empty space, catalogue entry mid-build — live
     * example: a product offer literally named "...Needs Attribute Mapping /
     * Pricing") is one flat clear colour end to end. getScreenshot() has no
     * way to know that itself — it succeeds either way — so this is what
     * tells the two apart before a dark square ever reaches a cart line.
     *
     * Downscaled to 16x16 first: only the spread matters, not fidelity, and a
     * tiny canvas keeps the decode cheap regardless of the source size.
     * Measured live: a real hood render's min/max brightness spread was 240
     * (0-255 scale); a dark, blank capture's was 17. 30 sits with wide margin
     * on both sides of that gap.
     */
    function isBlankImage(dataUrl) {
        return new Promise(function (resolve) {
            var img = new Image();
            img.onload = function () {
                try {
                    var size = 16;
                    var c = document.createElement('canvas');
                    c.width = size;
                    c.height = size;
                    var ctx = c.getContext('2d');
                    ctx.drawImage(img, 0, 0, size, size);
                    var data = ctx.getImageData(0, 0, size, size).data;
                    var min = 255;
                    var max = 0;
                    for (var i = 0; i < data.length; i += 4) {
                        var v = (data[i] + data[i + 1] + data[i + 2]) / 3;
                        if (v < min) min = v;
                        if (v > max) max = v;
                    }
                    resolve((max - min) < 30);
                } catch (e) {
                    resolve(true); // can't tell — fail closed, same as below
                }
            };
            img.onerror = function () { resolve(true); };
            img.src = dataUrl;
        });
    }

    function canonicalScreenshot() {
        var api = window.expivi;
        if (!api && window.expivi2dScreenshot) {
            // 2D product: composite the visualization layers (mount2dViewer).
            try {
                return Promise.resolve(window.expivi2dScreenshot());
            } catch (e) {
                return Promise.resolve(null);
            }
        }
        if (!api || !api.resolveProductNodes || !api.getScreenshot) {
            return Promise.resolve(null);
        }

        // The dominant real cause, found live: the viewer sits inside the
        // gallery's "3D VIEW" tab (hs-product-details.liquid), hidden via
        // display:none until the shopper clicks it. A hidden container
        // measures 0x0, so Babylon's canvas never gets a real drawing-buffer
        // size — getScreenshot() still "succeeds" but the capture is a flat,
        // empty square no matter how long the scene has had to load. If the
        // shopper never switched to that tab, there is nothing real to shoot.
        var container = document.querySelector('[data-expivi-viewer-container]');
        if (!container || !container.offsetWidth || !container.offsetHeight) {
            return Promise.resolve(null);
        }

        // __hoodslyReady is set by mount3dViewer() (expivi-viewer.js) only
        // after fully_loaded has fired AND fitCamera has settled — even with
        // a properly sized, visible container, resolveProductNodes() can
        // return nodes for geometry still streaming in (a glitched,
        // part-loaded capture). Checked here, not waited for: if the shopper
        // clicked before the viewer reached this point, skip straight to no
        // capture — same as any other failure, same safe variant-photo fallback.
        if (!api.__hoodslyReady) {
            return Promise.resolve(null);
        }

        // HARD CEILING. Nothing in here may hang the add to cart. Observed live:
        // a getScreenshot() already in flight makes the next one never settle,
        // and the whole promise chain stalls — the button stays disabled with
        // the loading class, no error, no request, forever. rAF is the other
        // way in: it does not fire in a backgrounded tab, so a shopper who
        // switches tab mid-add would wedge it too. On timeout we resolve null
        // and the caller falls back to Expivi's own thumbnail.
        return settleWithin(capturePose(api), CAPTURE_TIMEOUT_MS, null).then(function (shot) {
            if (!shot) {
                return null;
            }
            // Local decode of an already-in-memory data URL — no network, no
            // meaningful delay. Unable to decide within the bound -> fail
            // closed (treat as blank): the variant photo is always a safe
            // fallback, a dark square in the cart is not.
            return settleWithin(isBlankImage(shot), BLANK_CHECK_TIMEOUT_MS, true)
                .then(function (blank) { return blank ? null : shot; });
        });
    }

    function uploadPreview(dataUrl) {
        if (!dataUrl || String(dataUrl).indexOf('data:image') !== 0) {
            return Promise.resolve(null);
        }
        var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        // 8s, not 3s. Measured against the dev app through Shopify's proxy: a
        // trivial proxy GET that does NO image work already costs ~1.9s (browser
        // -> Shopify -> app), and a full upload landed at 1.6-3.0s. A 3s abort
        // cancelled real uploads, which shows up in devtools as a "cancelled"
        // request and silently loses every preview. The image pipeline is not
        // the cost here; the proxy hop is.
        var timer = setTimeout(function () {
            if (controller) { controller.abort(); }
        }, 8000);
        // App-proxy paths are absolute (never locale-prefixed like cartRoot()),
        // same as fetchWholesaleDiscount above.
        return fetch('/apps/hub/renders', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ image: dataUrl }),
            signal: controller ? controller.signal : undefined,
        })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (b) { return (b && b.success && b.url) || null; })
            .catch(function () { return null; })
            .then(function (url) {
                clearTimeout(timer);
                return typeof url === 'string' && url ? url : null;
            });
    }

    /**
     * Start downloading the render NOW, while the cart POST and the redirect to
     * /cart are still in flight.
     *
     * Without this the cart page sets the right src before paint but the browser
     * keeps showing the variant photo until the new image decodes — and on the
     * redirect that image has never been fetched, so it is a cold trip to the app
     * and the swap is visible. Every later reload is fine because the response is
     * immutable-cached for a year; the flicker only ever happened once, right
     * after add-to-cart. Warming it here means /cart usually reads from cache.
     *
     * Fire and forget: no await, no error handling. A failed warm just means the
     * cart page fetches it itself, which is today's behaviour.
     */
    function warmPreview(url) {
        try {
            var pre = new Image();
            pre.decoding = 'async';
            pre.src = url;
        } catch (e) {
            /* cosmetic — the cart page will fetch it */
        }
    }

    /**
     * Attach the uploaded render url, or nothing at all when the upload didn't
     * resolve — an absent property is what makes the line fall back to the
     * variant photo. Split out so the node assert check can pin both branches.
     */
    function attachPreview(properties, url) {
        if (url && typeof url === 'string') {
            properties._preview_url = url;
            warmPreview(url);
        }
        return properties;
    }

    var api = {
        discountedUnitPrice: discountedUnitPrice,
        discountedWithAddons: discountedWithAddons,
        unitPriceProperties: unitPriceProperties,
        cappedWholesalePercent: cappedWholesalePercent,
        excludedSum: excludedSum,
        breakdownAmounts: breakdownAmounts,
        rawPriceItems: rawPriceItems,
        rememberPriceItems: rememberPriceItems,
        valuePrice: valuePrice,
        rememberValuePrices: rememberValuePrices,
        fetchWholesaleDiscount: fetchWholesaleDiscount,
        configuredSkuCodes: configuredSkuCodes,
        resolveMainSku: resolveMainSku,
        buildDebugSku: buildDebugSku,
        actingCompanyId: actingCompanyId,
        capturePose: capturePose,
        normalizeKey: normalizeKey,
        allowedColorIdentifiers: allowedColorIdentifiers,
        correctedSelection: correctedSelection,
        staleSnapshotValueCid: staleSnapshotValueCid,
        shouldApplySnapshot: shouldApplySnapshot,
        isAllowedColor: isAllowedColor,
        isAlwaysShown: isAlwaysShown,
        ALWAYS_SHOWN_COUNT: ALWAYS_SHOWN_KEYS.length,
        nextFrames: nextFrames,
        settleWithin: settleWithin,
        poseOf: poseOf,
        applyPose: applyPose,
        canonicalScreenshot: canonicalScreenshot,
        uploadPreview: uploadPreview,
        warmPreview: warmPreview,
        attachPreview: attachPreview,
    };

    // Node (the *.test.js scripts) and the browser, from one file.
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = api;
    }
    if (typeof window !== 'undefined') {
        window.ExpiviHoodsly = api;
        // Before expivi-viewer.js boots the SDK, so the first price call is caught.
        observePriceItems();
    }
})();
